import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { db } from "../../config/firebase";

const COLLECTION_CONTAS_PAGAR = "financeiro_contas_pagar";

const STATUS_OPTIONS = ["PENDENTE", "PAGO", "VENCIDO", "CANCELADO"];

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FinanceiroContasPagarPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [campoFiltro, setCampoFiltro] = useState("descricao");
  const [textoFiltro, setTextoFiltro] = useState("");

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        setClientes(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  useEffect(() => {
    if (restringirEmpresa) {
      // no-op: empresaId não é mais utilizado
    }
  }, [restringirEmpresa, empresaEscopoId]);

  useEffect(() => {
    const carregar = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const snap = await getDocs(
          query(collection(db, COLLECTION_CONTAS_PAGAR), where("grupoId", "==", usuario.grupoId))
        );

        let lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (restringirEmpresa && empresaEscopoId) {
          lista = lista.filter((item) => String(item?.empresaId || "") === String(empresaEscopoId));
        }

        lista.sort((a, b) => String(a.vencimento || "").localeCompare(String(b.vencimento || ""), "pt-BR"));
        setRegistros(lista);
      } catch (e) {
        console.error("Erro ao carregar contas a pagar:", e);
        setErro("Erro ao carregar contas a pagar.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [usuario?.grupoId, restringirEmpresa, empresaEscopoId]);

  const camposFiltro = [
    { value: "descricao", label: "Descrição" },
    { value: "fornecedor", label: "Fornecedor" },
    { value: "categoria", label: "Categoria" },
    { value: "status", label: "Status" },
  ];

  const filtrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return registros;
    return registros.filter((r) => String(r?.[campoFiltro] ?? "").toLowerCase().includes(texto));
  }, [registros, campoFiltro, textoFiltro]);

  const handleExcluir = async (registro) => {
    const confirmar = window.confirm("Deseja excluir esta conta a pagar?");
    if (!confirmar) return;
    setErro("");
    setSucesso("");

    try {
      await deleteDoc(doc(db, COLLECTION_CONTAS_PAGAR, registro.id));
      setRegistros((prev) => prev.filter((item) => item.id !== registro.id));
      if (editandoId === registro.id) resetForm();
    } catch (e) {
      console.error("Erro ao excluir conta:", e);
      setErro("Erro ao excluir conta.");
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo="Contas a Pagar" subtitulo="Financeiro / Movimentação / Contas a Pagar">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Contas a pagar</h3>
            <p>Registros de despesas e obrigações financeiras com controle por vencimento.</p>
          </div>
          <div className="pc-table-header-actions">
            <button type="button" className="pc-btn-primario" onClick={() => navigate("/financeiro/contas-pagar/nova")}>
              + Adicionar conta
            </button>
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar contas a pagar..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          <table className="pc-table pc-table-expandable">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Fornecedor</th>
                <th>Categoria</th>
                <th>Vencimento</th>
                <th>Valor final</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={7} style={{ padding: 16 }}>Carregando contas...</td></tr>
              ) : filtrados.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16 }}>Nenhuma conta encontrada.</td></tr>
              ) : (
                filtrados.map((item) => {
                  const aberto = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>{item.descricao || "-"}</td>
                        <td>{item.fornecedor || "-"}</td>
                        <td>{item.categoria || "-"}</td>
                        <td>{item.vencimento || "-"}</td>
                        <td>R$ {formatMoney(item.valorFinal)}</td>
                        <td>{item.status || "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              className="pc-btn-secundario"
                              onClick={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                            >
                              {aberto ? "Ocultar" : "Detalhes"}
                            </button>
                            <button type="button" className="pc-btn-secundario" onClick={() => navigate(`/financeiro/contas-pagar/editar/${item.id}`)}>
                              Editar
                            </button>
                            <button type="button" className="pc-btn-perigo" onClick={() => handleExcluir(item)}>
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Empresa</div>
                                  <div className="pc-expand-value">{item.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Centro de custo</div>
                                  <div className="pc-expand-value">{item.centroCusto || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Competência</div>
                                  <div className="pc-expand-value">{item.competencia || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Data pagamento</div>
                                  <div className="pc-expand-value">{item.dataPagamento || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Valor original</div>
                                  <div className="pc-expand-value">R$ {formatMoney(item.valorOriginal || 0)}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Juros / Desconto</div>
                                  <div className="pc-expand-value">
                                    R$ {formatMoney(item.juros || 0)} / R$ {formatMoney(item.desconto || 0)}
                                  </div>
                                </div>
                                <div className="pc-expand-item" style={{ gridColumn: "1 / -1" }}>
                                  <div className="pc-expand-label">Observações</div>
                                  <div className="pc-expand-value">{item.observacoes || "-"}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
